import Vue from 'vue'
import axios from '@/axios-auth'

const state = {
  contracts: [],
  contractsByAddress: [],
  contractsByContactPerson: [],
  contractCategories: [
    'INSURANCE',
    'FINANCE',
    'HABITATION_HOUSEHOLD_AND_COMMUNICATION',
    'LEISURE_AND_CLUB',
    'SUBSCRIPTION',
    'ONLINE_SUBSCRIPTION_AND_SOCIAL_MEDIA_ACCOUNT',
    'OTHER'
  ],
  currentContract: {},
  estateAdministrationTypes: [
    'TERMINATE_CONTRACT',
    'TRANSFER_TO_PERSON_OF_TRUST'
  ]
}

const getters = {
  currentContract: state => state.currentContract,
  contracts: state => state.contracts,
  contractsByAddress: state => state.contractsByAddress,
  contractsByContactPerson: state => state.contractsByContactPerson,
  contractCategories: state => state.contractCategories,
  estateAdministrationTypes: state => state.estateAdministrationTypes
}

const actions = {
  deleteContract ({getters, dispatch, commit}, id) {
    return new Promise((resolve, reject) => {
      const currentUser = getters.currentUser
      const idToken = currentUser.idToken
      const authValue = 'Bearer '.concat(idToken)
      axios.delete(`/contracts/${id}`, {
        headers: {
          Authorization: authValue
        }
      }).then(() => {
        commit('deleteContract', id)
        dispatch('setLogoutTimer')
        resolve()
      }).catch((err) => reject(err))
    })
  },
  fetchContractById ({getters, state, dispatch, commit}, payload) {
    return new Promise((resolve, reject) => {
      const contract = state.contracts.find(contract => contract.id === payload.id)
      if (contract && !payload.forceFetch) {
        commit('setCurrentContract', contract)
        resolve()
      } else {
        const currentUser = getters.currentUser
        const idToken = currentUser.idToken
        const authValue = 'Bearer '.concat(idToken)
        axios.get(`/contracts/${payload.id}`, {
          headers: {
            Authorization: authValue
          }
        }).then((response) => {
          dispatch('setLogoutTimer')
          commit('setCurrentContract', response.data)
          resolve()
        }).catch((err) => {
          reject(err)
        })
      }
    })
  },
  fetchContracts ({commit, dispatch, getters}) {
    return new Promise((resolve, reject) => {
      const currentUser = getters.currentUser
      const idToken = currentUser.idToken
      const authValue = 'Bearer '.concat(idToken)
      axios.get('/contracts', {
        headers: {
          Authorization: authValue
        }
      }).then(response => {
        commit('setContracts', response.data)
        dispatch('setLogoutTimer')
        resolve()
      }).catch((err) => reject(err))
    })
  },
  fetchContractsByAddress ({getters, dispatch, state, commit}, addressId) {
    return new Promise((resolve, reject) => {
      const currentUser = getters.currentUser
      const idToken = currentUser.idToken
      const authValue = 'Bearer '.concat(idToken)
      axios.get('/contracts', {
        headers: {
          Authorization: authValue
        },
        params: {
          addressId
        }
      }).then((response) => {
        commit('setContractsByAddress', response.data)
        dispatch('setLogoutTimer')
        resolve()
      }).catch((err) => {
        reject(err)
      })
    })
  },
  fetchContractsByContactPerson ({getters, dispatch, commit}, contactPersonId) {
    return new Promise((resolve, reject) => {
      const currentUser = getters.currentUser
      const idToken = currentUser.idToken
      const authValue = 'Bearer '.concat(idToken)
      axios.get('/contracts', {
        headers: {
          Authorization: authValue
        },
        params: {
          contactPersonId
        }
      }).then((response) => {
        commit('setContractsByContactPerson', response.data)
        dispatch('setLogoutTimer')
        resolve()
      }).catch((err) => reject(err))
    })
  },
  storeContract ({commit, dispatch, getters}, contract) {
    // Clone contactPerson to prevent serialization error
    const clone = {...contract}
    delete clone.contactPerson
    if (contract.contactPerson && contract.contactPerson.id) {
      clone.contactPerson = {
        id: contract.contactPerson.id
      }
    }
    return new Promise((resolve, reject) => {
      const currentUser = getters.currentUser
      const idToken = currentUser.idToken
      const authValue = 'Bearer '.concat(idToken)
      axios.post('/contracts', clone, {
        headers: {
          Authorization: authValue
        }
      }).then((response) => {
        commit('addContract', response.data)
        dispatch('setLogoutTimer')
        resolve(response.data.isComplete)
      }).catch((err) => {
        reject(err)
      })
    })
  },
  updateContract ({getters, commit, dispatch}, contract) {
    const clone = {...contract}
    delete clone.contactPerson
    if (contract.contactPerson && contract.contactPerson.id) {
      clone.contactPerson = {
        id: contract.contactPerson.id
      }
    }
    return new Promise((resolve, reject) => {
      const currentUser = getters.currentUser
      const idToken = currentUser.idToken
      const authValue = 'Bearer '.concat(idToken)
      axios.put(`/contracts/${contract.id}`, clone, {
        headers: {
          Authorization: authValue
        }
      }).then((response) => {
        commit('updateContract', response.data)
        commit('setCurrentContract', {})
        dispatch('setLogoutTimer')
        resolve(response.data.isComplete)
      }).catch((err) => {
        reject(err)
      })
    })
  },
  updateContractsWithAddress ({getters, commit, dispatch}, data) {
    return new Promise((resolve, reject) => {
      const currentUser = getters.currentUser
      const idToken = currentUser.idToken
      const authValue = 'Bearer '.concat(idToken)
      const dto = {
        currentAddressId: data.currentAddressId,
        newAddressId: data.newAddressId
      }
      axios.put(`/contracts/changeAddress/`, dto, {
        headers: {
          Authorization: authValue
        },
        params: {
          oldAddressId: data.oldAddressId,
          newAddressId: data.newAddressId
        }
      }).then(() => {
        commit('setContractsByAddress', [])
        dispatch('setLogoutTimer')
        resolve()
      }).catch((err) => {
        reject(err)
      })
    })
  }
}

const mutations = {
  addContract (state, contract) {
    state.contracts.push(contract)
  },
  deleteAccountLink (state) {
    Vue.delete(state.currentContract, 'accountLink')
  },
  deleteAccountNumber (state) {
    Vue.delete(state.currentContract, 'accountNumber')
  },
  deleteAccountUsername (state) {
    Vue.delete(state.currentContract, 'accountUsername')
  },
  deleteCancellationPeriod (state) {
    Vue.delete(state.currentContract, 'cancellationPeriod')
  },
  deleteContract (state, id) {
    const contractIndex = state.contracts.findIndex(contract => contract.id === id)
    Vue.delete(state.contracts, contractIndex)
  },
  deleteConclusionDate (state) {
    Vue.delete(state.currentContract, 'conclusionDate')
  },
  deleteContractDuration (state) {
    Vue.delete(state.currentContract, 'contractDuration')
  },
  deleteContractNumber (state) {
    Vue.delete(state.currentContract, 'contractNumber')
  },
  deleteInsuredPersonNumber (state) {
    Vue.delete(state.currentContract, 'insuredPersonNumber')
  },
  setCurrentContract (state, contract) {
    state.currentContract = contract
  },
  setContracts (state, contracts) {
    state.contracts = contracts
  },
  setContractsByAddress (state, contracts) {
    state.contractsByAddress = contracts
  },
  setContractsByContactPerson (state, contracts) {
    state.contractsByContactPerson = contracts
  },
  updateConclusionDate (state, date) {
    Vue.set(state.currentContract, 'conclusionDate', date)
  },
  updateContract (state, contract) {
    const contractToUpdateIndex = state.contracts.findIndex(contractToUpdate => contractToUpdate.id === contract.id)
    Vue.set(state.contracts, contractToUpdateIndex, contract)
  },
  updateContractCategory (state, category) {
    Vue.set(state.currentContract, 'contractCategory', category)
  },
  updateContactPerson (state, contactPerson) {
    Vue.set(state.currentContract, 'contactPerson', contactPerson)
  },
  updateContractPartner (state, contractPartner) {
    Vue.set(state.currentContract, 'contractPartner', contractPartner)
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/contracts.js